import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import publicRequest from "../../configs/axios";

// ── Cart persistence (localStorage) ─────────────────────────────────────
// Loads the saved cart so items survive page refreshes and revisits.
const CART_STORAGE_KEY = "bookmarket_cart";

const loadCartFromStorage = () => {
  try {
    if (typeof window === "undefined") return [];
    const saved = window.localStorage.getItem(CART_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    return [];
  }
};

const saveCartToStorage = (cart) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart || []));
  } catch (e) {
    // storage full / unavailable — ignore
  }
};

const initialState = {
  productsState: [],
  queryProductsState: [],
  allProductsState: [],
  searchProductsState: [],
  popularState: [],
  totalProductsCount: 0,
  totalQueryProductsCount: 0,
  totalAllProductsCount: 0,
  totalPopularCount: 0,
  cart: loadCartFromStorage(),
  mobileNavVisible: true,
  status: "idle", // for paginated productsState
  allStatus: "idle", // for allProductsState
  queryStatus: "idle", // for queryProductsState (categories/publishers)
  searchStatus: "idle", // for searchProductsState
  idStatus: "idle", // for selectedProduct (single product)
  popularStatus: "idle", // for popularState (server-side popular sort)
  homeStatus: "idle", // for homeBundle (home page above-the-fold sections)
  homeBundle: { banners: [], recent: [], popular: [], groups: [] },
  error: "",
  selectedProduct: null,
};

export const postProductAsync = createAsyncThunk(
  "products/postProductAsync",
  async (payload) => {
    const response = await publicRequest.post(`/add-product`, payload);
    return response.data;
  },
);

export const loadProductsAsync = createAsyncThunk(
  "products/loadProductsAsync",
  async (payload) => {
    const { page, limit, status } = payload || {};
    const params = new URLSearchParams();
    params.set("page", page || 0);
    params.set("limit", limit || 0);
    if (status) params.set("status", status);
    const response = await publicRequest.get(`/all-products?${params.toString()}`);
    return response.data;
  },
  {
    condition: (payload, { getState }) => {
      const { status } = getState().products;
      if (status === "Pending") return false;
    },
  },
);

export const searchProductsAsync = createAsyncThunk(
  "products/searchProductsAsync",
  async (payload) => {
    const { search, limit, status } = payload || {};
    const params = new URLSearchParams();
    params.set("search", search || "");
    params.set("limit", limit || 0);
    if (status) params.set("status", status);
    const response = await publicRequest.get(`/all-products?${params.toString()}`);
    return response.data;
  },
);

export const fetchAllProductsAsync = createAsyncThunk(
  "products/fetchAllProductsAsync",
  async () => {
    const response = await publicRequest.get("/all-products?limit=0");
    return response.data;
  },
  {
    condition: (_, { getState }) => {
      const { allStatus } = getState().products;
      if (allStatus === "Pending" || allStatus === "Success") return false;
    },
  },
);

// Fetches every above-the-fold section of the home page in ONE small,
// pre-warmed and cached request (banners, recent, popular, and the curated
// showcase-grid rows). Replaces downloading the entire catalog just to render
// the first screen — less network, lower browser RAM, and no full-catalog DB
// scan per visitor.
export const loadHomeBundleAsync = createAsyncThunk(
  "products/loadHomeBundleAsync",
  async () => {
    const response = await publicRequest.get("/home-bundle");
    return response.data;
  },
  {
    condition: (_, { getState }) => {
      const { homeStatus } = getState().products;
      if (homeStatus === "Pending" || homeStatus === "Success") return false;
    },
  },
);

export const loadQueryProductsAsync = createAsyncThunk(
  "products/loadQueryProductsAsync",
  async (payload) => {
    const { category, page, limit } = payload || {};
    const response = await publicRequest.get(
      `/all-products?category=${category || payload}&page=${page || 0}&limit=${
        limit || 0
      }`,
    );
    return response.data;
  },
);

// Add loadPublicationProductsAsync
export const loadPublicationProductsAsync = createAsyncThunk(
  "products/loadPublicationProductsAsync",
  async (payload) => {
    const { publisher, page, limit } = payload || {};
    const response = await publicRequest.get(
      `/all-products?publisher=${publisher || payload}&page=${page || 0}&limit=${
        limit || 0
      }`,
    );
    return response.data;
  },
);

// Add product CRUD thunks
export const loadPopularProductsAsync = createAsyncThunk(
  "products/loadPopularProductsAsync",
  async (payload) => {
    const { limit } = payload || {};
    const params = new URLSearchParams();
    params.set("sort", "popular");
    params.set("page", 0);
    params.set("limit", limit || 30);
    // Published = in stock and not a draft, so the popular list only
    // contains buyable products.
    params.set("status", "publish");
    const response = await publicRequest.get(`/all-products?${params.toString()}`);
    return response.data;
  },
  {
    condition: (_, { getState }) => {
      const { popularStatus } = getState().products;
      if (popularStatus === "Pending" || popularStatus === "Success") return false;
    },
  },
);

export const getProductByIdAsync = createAsyncThunk(
  "products/getProductByIdAsync",
  async (id) => {
    const response = await publicRequest.get(`/product/${id}`);
    return response.data;
  },
  {
    condition: (id, { getState }) => {
      const { selectedProduct, idStatus } = getState().products;
      if (idStatus === "Pending") return false;
      if (idStatus === "Success" && selectedProduct?._id === id) return false;
    },
  },
);

export const updateProductAsync = createAsyncThunk(
  "products/updateProductAsync",
  async ({ id, data }) => {
    const response = await publicRequest.put(`/product/${id}`, data);
    return { ...response.data, id, data };
  },
);

export const deleteProductAsync = createAsyncThunk(
  "products/deleteProductAsync",
  async (id) => {
    const response = await publicRequest.delete(`/product/${id}`);
    return { ...response.data, id };
  },
);

export const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    addToCart: (state, { payload }) => {
      const existingIndex = state.cart.findIndex(
        (pd) => pd.item?._id === payload.item?._id,
      );
      if (existingIndex !== -1) {
        // Already in cart → increase quantity instead of duplicating
        const existing = state.cart[existingIndex];
        existing.quantity = Number(existing.quantity) + (Number(payload.quantity) || 1);
        const price =
          payload.item.discountPrice &&
          Number(payload.item.discountPrice) < Number(payload.item.price)
            ? Number(payload.item.discountPrice)
            : Number(payload.item.price);
        existing.totalPrice = Number(existing.quantity) * price;
      } else {
        state.cart.push(payload);
      }
      saveCartToStorage(state.cart);
    },
    emptyCart: (state, action) => {
      state.cart = [];
      saveCartToStorage(state.cart);
    },
    emptyPrev: (state, action) => {
      state.queryProductsState = [];
    },
    setMobileNavVisible: (state, { payload }) => {
      state.mobileNavVisible = !!payload;
    },
    updateSelectedProduct: (state, { payload }) => {
      if (state.selectedProduct) {
        state.selectedProduct = { ...state.selectedProduct, ...payload };
      }
    },
    resetSelectedProduct: (state) => {
      state.selectedProduct = null;
      state.idStatus = "idle";
    },
    handleCancelOrder: (state, { payload }) => {
      state.cart = state.cart.filter((pd) => pd.item._id !== payload);
      saveCartToStorage(state.cart);
    },
    handleIncrease: (state, { payload }) => {
      state.cart = state.cart.map((item) => {
        let items = item;
        if (item.item._id === payload) {
          item.quantity = item.quantity + 1;
          const price =
            item.item.discountPrice &&
            Number(item.item.discountPrice) < Number(item.item.price)
              ? Number(item.item.discountPrice)
              : Number(item.item.price);
          item.totalPrice = Number(item.quantity) * price;
          items = { ...item };
        }
        return items;
      });
      saveCartToStorage(state.cart);
    },
    handleDecrease: (state, { payload }) => {
      state.cart = state.cart.map((item) => {
        let items = item;
        if (item.item._id === payload && item.quantity > 1) {
          item.quantity = item.quantity - 1;
          const price =
            item.item.discountPrice &&
            Number(item.item.discountPrice) < Number(item.item.price)
              ? Number(item.item.discountPrice)
              : Number(item.item.price);
          item.totalPrice = Number(item.quantity) * price;
          items = { ...item };
        }
        return items;
      });
      saveCartToStorage(state.cart);
    },
    productSorting: (state, { payload }) => {
      const sorter = (pd1, pd2) => {
        if (payload === "low") {
          return +pd1.price - +pd2.price;
        } else if (payload === "high") {
          return +pd2.price - +pd1.price;
        }
      };
      state.queryProductsState.sort(sorter);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postProductAsync.pending, (state, action) => {
      toast.loading("Processing... Please Wait!!");
    });
    builder.addCase(postProductAsync.fulfilled, (state, { payload }) => {
      toast.dismiss();
      toast.success("Your Product is Successfully added!!!");
    });
    builder.addCase(
      postProductAsync.rejected,
      (state, { error: { message } }) => {
        toast.dismiss();
        toast.error(message);
      },
    );
    builder.addCase(loadProductsAsync.pending, (state, action) => {
      state.status = "Pending";
    });
    builder.addCase(loadProductsAsync.fulfilled, (state, { payload }) => {
      state.productsState = Array.isArray(payload?.data) ? payload.data : [];
      state.totalProductsCount = payload?.count || 0;
      state.status = "Success";
    });
    builder.addCase(
      loadProductsAsync.rejected,
      (state, { error: { message } }) => {
        state.status = "Rejected";
        state.error = message;
      },
    );
    builder.addCase(searchProductsAsync.pending, (state) => {
      state.searchStatus = "Pending";
    });
    builder.addCase(searchProductsAsync.fulfilled, (state, { payload }) => {
      state.searchProductsState = Array.isArray(payload?.data)
        ? payload.data
        : [];
      state.searchStatus = "Success";
    });
    builder.addCase(
      searchProductsAsync.rejected,
      (state, { error: { message } }) => {
        state.searchStatus = "Rejected";
        state.error = message;
      },
    );
    builder.addCase(loadQueryProductsAsync.pending, (state, action) => {
      state.queryStatus = "Pending";
    });
    builder.addCase(loadQueryProductsAsync.fulfilled, (state, { payload }) => {
      state.queryProductsState = Array.isArray(payload?.data)
        ? payload.data
        : [];
      state.totalQueryProductsCount = payload?.count || 0;
      state.queryStatus = "Success";
    });
    builder.addCase(
      loadQueryProductsAsync.rejected,
      (state, { error: { message } }) => {
        state.queryStatus = "Rejected";
        state.error = message;
      },
    );
    builder.addCase(loadPublicationProductsAsync.pending, (state, action) => {
      state.queryStatus = "Pending";
    });
    builder.addCase(
      loadPublicationProductsAsync.fulfilled,
      (state, { payload }) => {
        state.queryProductsState = Array.isArray(payload?.data)
          ? payload.data
          : [];
        state.totalQueryProductsCount = payload?.count || 0;
        state.queryStatus = "Success";
      },
    );
    builder.addCase(
      loadPublicationProductsAsync.rejected,
      (state, { error: { message } }) => {
        state.queryStatus = "Rejected";
        state.error = message;
      },
    );

    // fetch all products
    builder.addCase(fetchAllProductsAsync.pending, (state) => {
      state.allStatus = "Pending";
    });
    builder.addCase(fetchAllProductsAsync.fulfilled, (state, { payload }) => {
      state.allProductsState = Array.isArray(payload?.data) ? payload.data : [];
      state.totalAllProductsCount = payload?.count || 0;
      state.allStatus = "Success";
    });
    builder.addCase(
      fetchAllProductsAsync.rejected,
      (state, { error: { message } }) => {
        state.allStatus = "Rejected";
        state.error = message;
      },
    );

    // home bundle (one request → all above-the-fold sections)
    builder.addCase(loadHomeBundleAsync.pending, (state) => {
      state.homeStatus = "Pending";
    });
    builder.addCase(loadHomeBundleAsync.fulfilled, (state, { payload }) => {
      state.homeBundle = payload || {
        banners: [],
        recent: [],
        popular: [],
        groups: [],
      };
      state.homeStatus = "Success";
    });
    builder.addCase(loadHomeBundleAsync.rejected, (state) => {
      state.homeStatus = "Rejected";
    });

    // popular products (server-side sort)
    builder.addCase(loadPopularProductsAsync.pending, (state) => {
      state.popularStatus = "Pending";
    });
    builder.addCase(loadPopularProductsAsync.fulfilled, (state, { payload }) => {
      state.popularState = Array.isArray(payload?.data) ? payload.data : [];
      state.totalPopularCount = payload?.count || 0;
      state.popularStatus = "Success";
    });
    builder.addCase(
      loadPopularProductsAsync.rejected,
      (state, { error: { message } }) => {
        state.popularStatus = "Rejected";
        state.error = message;
      },
    );

    // get by id
    builder.addCase(getProductByIdAsync.pending, (state) => {
      state.idStatus = "Pending";
    });
    builder.addCase(getProductByIdAsync.fulfilled, (state, { payload }) => {
      state.selectedProduct = payload;
      state.idStatus = "Success";
    });
    builder.addCase(
      getProductByIdAsync.rejected,
      (state, { error: { message } }) => {
        state.idStatus = "Rejected";
        state.error = message;
      },
    );

    // update product
    builder.addCase(updateProductAsync.pending, () => {
      toast.loading("Updating product...");
    });
    builder.addCase(
      updateProductAsync.fulfilled,
      (state, { meta: { arg } }) => {
        toast.dismiss();
        toast.success("Product updated successfully!");
        const { id, data } = arg || {};
        state.productsState = state.productsState.map((pd) =>
          pd._id === id ? { ...pd, ...data } : pd,
        );
        state.queryProductsState = state.queryProductsState.map((pd) =>
          pd._id === id ? { ...pd, ...data } : pd,
        );
        state.searchProductsState = state.searchProductsState.map((pd) =>
          pd._id === id ? { ...pd, ...data } : pd,
        );
      },
    );
    builder.addCase(
      updateProductAsync.rejected,
      (state, { error: { message } }) => {
        toast.dismiss();
        toast.error(message);
      },
    );

    // delete product
    builder.addCase(deleteProductAsync.pending, () => {
      toast.loading("Deleting product...");
    });
    builder.addCase(
      deleteProductAsync.fulfilled,
      (state, { meta: { arg } }) => {
        toast.dismiss();
        toast.success("Product deleted successfully!");
        const id = arg;
        state.productsState = state.productsState.filter((pd) => pd._id !== id);
        state.queryProductsState = state.queryProductsState.filter(
          (pd) => pd._id !== id,
        );
        state.searchProductsState = state.searchProductsState.filter(
          (pd) => pd._id !== id,
        );
      },
    );
    builder.addCase(
      deleteProductAsync.rejected,
      (state, { error: { message } }) => {
        toast.dismiss();
        toast.error(message);
      },
    );
  },
});

export const {
  addToCart,
  emptyCart,
  emptyPrev,
  handleCancelOrder,
  handleIncrease,
  handleDecrease,
  productSorting,
  setMobileNavVisible,
  updateSelectedProduct,
  resetSelectedProduct,
} = productsSlice.actions;

export default productsSlice.reducer;
